/**
 * A7Y-5499 — what the AI Discovery engine learned about whether its own fetch
 * and AI search crawlers can reach the publisher's website. Carried on
 * `consistency_report` (`fetch_failures`, `crawler_access`); never scored.
 */

/**
 * Why a website fetch failed. Mirrors `WebsiteFetchFailureReason` in
 * `@a7y/url-validation`, which produces it.
 */
export type WebsiteFetchFailureReason =
  | 'blocked_by_bot_protection'
  | 'javascript_only_page'
  | 'redirects_to_homepage'
  | 'disallowed_by_robots_txt'
  | 'http_error'
  | 'timeout'
  | 'unreachable';

export interface WebsiteFetchFailure {
  /** The page we tried to read. */
  url: string;
  reason: WebsiteFetchFailureReason;
  status_code?: number;
  /** Where a redirect landed, when it did. */
  final_url?: string;
  /**
   * A7Y-6001 — true when the page was also tried through a rendering browser
   * (Cloudflare Browser Run) after a bot challenge, and that failed too.
   * Absent when no fallback was tried (no token bound, or a report written
   * before A7Y-6001), and the copy then never claims one was.
   */
  browser_run_attempted?: boolean;
}

/** One AI search crawler a confirmed domain's robots.txt keeps out. */
export interface CrawlerAccessFinding {
  /** `https://example.com` */
  origin: string;
  robots_url: string;
  /** The crawler's robots.txt product token, e.g. `OAI-SearchBot`. */
  user_agent: string;
  /** e.g. `OpenAI` */
  vendor: string;
  /** The surface the crawler feeds, e.g. `ChatGPT search`. */
  product: string;
  /** The vendor's crawler documentation. */
  doc_url: string;
  /** What the block costs the publisher; completes "so …" (e.g. `ChatGPT search cannot read or cite your site`). */
  consequence: string;
  /** The path the rule blocks (`/` when the whole site is blocked). */
  path: string;
  /** `*` when the block comes from the catch-all group, else the crawler's own token. */
  matched_group: string;
  /** The winning rule as written, e.g. `Disallow: /`. */
  rule: string;
}

export interface CrawlerAccessCheckedDomain {
  origin: string;
  robots_url: string;
  /** `absent` allows every crawler; `unreachable` means nothing could be concluded. */
  robots_status: 'found' | 'absent' | 'unreachable';
}

export interface CrawlerAccessReport {
  checked: CrawlerAccessCheckedDomain[];
  findings: CrawlerAccessFinding[];
}

/**
 * A7Y-6001 — one confirmed-website domain this run's AI answers cited: the
 * positive evidence that AI search reached the site, whatever our own reader
 * managed. Derived from the run's citations (`modules/citations`), never from
 * a request posing as an AI crawler.
 */
export interface WebsiteAiReach {
  /** Registrable domain, e.g. `obdeleven.com`. */
  domain: string;
  /** Citations across every LLM in the run. */
  citations: number;
  /** Citations per LLM, keyed by the engine's LLM name (e.g. `chatgpt`). */
  by_llm: Record<string, number>;
}

/**
 * A7Y-5433 — how a bot-protection refusal reads: "<page> refused our crawler (HTTP 403)", or, when
 * the site answered 2xx with a bot-challenge page, "<page> answered our crawler with a bot-challenge
 * page". "refused … (HTTP 200)" on Staging audit U4dnLA0ytfKtEkpP8yiK read as a contradiction.
 */
export function botProtectionRefusal(
  failure: Pick<WebsiteFetchFailure, 'url' | 'status_code'>
): string {
  const status = failure.status_code;
  if (status !== undefined && status >= 200 && status < 300) {
    return `${failure.url} answered our crawler with a bot-challenge page instead of the page`;
  }
  return `${failure.url} refused our crawler${status ? ` (HTTP ${status})` : ''}`;
}

/**
 * The one sentence every surface prints for a failed website read, so the
 * panel and the shared audit view cannot drift.
 */
export function websiteFetchFailureSentence(failure: WebsiteFetchFailure): string {
  const page = failure.url;
  switch (failure.reason) {
    case 'blocked_by_bot_protection':
      // A7Y-6001 — our limit, never the client's: whether an AI search crawler
      // gets through is read from robots.txt only (TPF, 2026-10-28).
      return `${botProtectionRefusal(failure)}${failure.browser_run_attempted ? ', even through a rendering browser' : ''}, so this page was not read. That is a limit of our reader; whether AI search crawlers get through is read from your robots.txt only.`;
    case 'javascript_only_page':
      return `${page} shows under 100 characters of text until JavaScript runs, so crawlers that do not run scripts see an empty page.`;
    case 'redirects_to_homepage':
      return `${page} redirects to your homepage${failure.final_url ? ` (${failure.final_url})` : ''}, so the page itself could not be read.`;
    case 'disallowed_by_robots_txt':
      return `Your robots.txt asks our crawler not to read ${page}, so we did not.`;
    case 'http_error':
      return `${page} returned ${failure.status_code ? `HTTP ${failure.status_code}` : 'an HTTP error'}.`;
    case 'timeout':
      return `${page} did not respond in time.`;
    case 'unreachable':
      return `We could not connect to ${page}.`;
  }
}

/** The one sentence every surface prints for a crawler a robots.txt keeps out. */
export function crawlerAccessFindingSentence(finding: CrawlerAccessFinding): string {
  return `Your robots.txt at ${finding.robots_url} disallows ${finding.user_agent} on "${finding.path}" (${finding.rule}), so ${finding.consequence}.`;
}

/** A7Y-6001 — a reader-facing LLM name for the AI-reach sentence. */
const LLM_DISPLAY_NAME: Record<string, string> = {
  chatgpt: 'ChatGPT',
  openai: 'ChatGPT',
  claude: 'Claude',
  anthropic: 'Claude',
  gemini: 'Gemini',
  google: 'Gemini',
  perplexity: 'Perplexity',
  google_ai_overview: 'Google AI Overviews',
};

/**
 * A7Y-6001 — the reader-facing name, or `null` for an LLM key with no entry:
 * an engine key such as `copilot` never reaches a client sentence; the caller
 * drops that clause and falls back to the unnamed aggregate.
 */
function llmDisplayName(llm: string): string | null {
  return LLM_DISPLAY_NAME[llm.toLowerCase()] ?? null;
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/**
 * A7Y-6001 — the one sentence that says AI search reached the site this run:
 * "AI search reached your site: ChatGPT answers linked obdeleven.com 8 times
 * and Perplexity answers linked it 2 times in this run." `null` when nothing
 * was cited — a true absence, never a block.
 */
export function websiteAiReachSentence(
  reach: ReadonlyArray<WebsiteAiReach> | undefined
): string | null {
  const cited = (reach ?? []).filter((r) => r.citations > 0);
  if (cited.length === 0) return null;
  const parts = cited.map((r) => {
    const named = Object.entries(r.by_llm)
      .filter(([, n]) => n > 0)
      .map(([llm, n]) => ({ llm, n, name: llmDisplayName(llm) }));
    for (const { llm } of named.filter((e) => e.name === null)) {
      console.info(
        `[website-access] no display name for LLM key "${llm}"; left out of the AI-reach sentence`
      );
    }
    // An unmapped key is never printed; when any key is unmapped the per-LLM
    // clauses would undercount, so the sentence uses the unnamed total.
    const perLlm = named.some((e) => e.name === null)
      ? []
      : named
          .sort((a, b) => b.n - a.n || a.llm.localeCompare(b.llm))
          .map(
            ({ name, n }, i) =>
              `${name} answers linked ${i === 0 ? r.domain : 'it'} ${n} ${n === 1 ? 'time' : 'times'}`
          );
    return perLlm.length > 0
      ? perLlm.join(' and ')
      : `AI answers linked ${r.domain} ${r.citations} ${r.citations === 1 ? 'time' : 'times'}`;
  });
  return `AI search reached your site: ${parts.join('; ')} in this run.`;
}

/** The fields `websiteReadSummary` reads off a consistency report. */
export interface WebsiteReadSummaryInput {
  website_available: boolean;
  analyzed_urls: string[];
  fetch_failures?: WebsiteFetchFailure[];
  ai_reach?: WebsiteAiReach[];
}

function failureClause(failure: WebsiteFetchFailure): string {
  switch (failure.reason) {
    case 'blocked_by_bot_protection':
      return `could not be read past its bot protection${failure.browser_run_attempted ? ', even through a rendering browser' : ''}`;
    case 'javascript_only_page':
      return 'shows under 100 characters of text until JavaScript runs';
    case 'redirects_to_homepage':
      return 'redirects to the homepage';
    case 'disallowed_by_robots_txt':
      return 'is kept out by your robots.txt';
    case 'http_error':
      return failure.status_code
        ? `returned HTTP ${failure.status_code}`
        : 'returned an HTTP error';
    case 'timeout':
      return 'did not respond in time';
    case 'unreachable':
      return 'could not be reached';
  }
}

/**
 * A7Y-6001 — the one plain line every surface prints about the website read
 * when a page could not be read: the panel, the shared audit view, the PDF
 * scorecard and the Markdown export. Names what was not read, that website
 * consistency and website placement were therefore not scored (both need the
 * page), that the robots.txt check still ran, and — when the run's AI answers cited the
 * site — that AI search reached it. `null` when every page was read, or when
 * no website was attempted (those surfaces have their own lines).
 */
export function websiteReadSummary(
  report: WebsiteReadSummaryInput | null | undefined
): string | null {
  if (!report) return null;
  const failures = report.fetch_failures ?? [];
  if (failures.length === 0) return null;
  const reach = websiteAiReachSentence(report.ai_reach);
  const described = failures.map((f) => `${f.url} ${failureClause(f)}`).join('; ');
  if (report.website_available === false) {
    const hosts = Array.from(new Set(failures.map((f) => hostOf(f.url))));
    const what =
      failures.length === 1 ? described : `${hosts.join(', ')} could not be read (${described})`;
    const line = `Website not read: ${what}. Website consistency and website placement need the page, so they were not scored; the robots.txt check ran.`;
    return reach ? `${line} ${reach}` : line;
  }
  const read = report.analyzed_urls.length;
  const line = `Website read from ${read} ${read === 1 ? 'page' : 'pages'}; ${failures.length} ${failures.length === 1 ? 'page' : 'pages'} could not be read: ${described}.`;
  return reach ? `${line} ${reach}` : line;
}
